<template>
  <div>
    <v-row class="fill-height">
      <v-col>
        <v-sheet height="64">
          <v-toolbar flat>
            <v-btn
              outlined
              class="mr-4"
              color="grey darken-2"
              @click="setToday"
            >
              {{ $t('BOOKING.SCHEDULE.TODAY') }}
            </v-btn>
            <v-btn fab text small color="grey darken-2" @click="prev">
              <v-icon small>
                mdi-chevron-left
              </v-icon>
            </v-btn>
            <v-btn fab text small color="grey darken-2" @click="next">
              <v-icon small>
                mdi-chevron-right
              </v-icon>
            </v-btn>
            <v-toolbar-title v-if="$refs.calendar">
              {{ $refs.calendar.title }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-menu bottom right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined color="grey darken-2" v-bind="attrs" v-on="on">
                  <span>{{ typeToLabel[type] }}</span>

                  <v-icon right>
                    mdi-menu-down
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="type = 'day'">
                  <v-list-item-title>{{
                    $t('BOOKING.SCHEDULE.DAY')
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="type = 'week'">
                  <v-list-item-title>{{
                    $t('BOOKING.SCHEDULE.WEEK')
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="type = 'month'">
                  <v-list-item-title>{{
                    $t('BOOKING.SCHEDULE.MONTH')
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="type = '4day'">
                  <v-list-item-title>{{
                    $t('BOOKING.SCHEDULE.DAYS')
                  }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>
        </v-sheet>
        <v-sheet height="600">
          <v-calendar
            ref="calendar"
            v-model="focus"
            class="schedule"
            color="primary"
            :events="events"
            :locale="lang"
            :weekdays="weekdays"
            :event-color="getEventColor"
            :type="type"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="updateData"
          >
            <template #event="{event}">
              <v-row :class="blockDayClass(event)" dense>
                <v-col class="pa-0">
                  <span v-if="isBlockEvent(event)">
                    {{ $t('PLANNINGS.TASKS.BLOCK.TIME') }}:
                  </span>

                  <span v-if="!isBlockEvent(event)"
                    >{{ $t('BOOKING.RESERVATIONS.ITEM') }}:
                  </span>
                  {{ eventTime(event) }}
                </v-col>
              </v-row>
            </template>
          </v-calendar>

          <v-menu
            v-model="selectedOpen"
            :close-on-content-click="false"
            :activator="selectedElement"
            offset-x
          >
            <v-card color="grey lighten-4" min-width="350px" flat>
              <v-toolbar :color="selectedEvent.color" dark>
                <v-btn icon @click="showSelectedEvent">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-toolbar-title
                  v-if="
                    selectedEvent.end - selectedEvent.start >
                      1000 * 60 * 60 * 24
                  "
                >
                  {{ selectedEvent.start | dateTimeFormat }} -
                  {{ selectedEvent.end | dateTimeFormat }}
                </v-toolbar-title>
                <v-toolbar-title v-else
                  >{{ selectedEvent.start | dateTimeFormat }} -
                  {{ selectedEvent.end | timeFormat }}</v-toolbar-title
                >
                <v-spacer></v-spacer>
                <v-btn icon>
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text v-if="isBlockEvent(selectedEvent)">
                <span class="text-h6">
                  {{ $t('PLANNINGS.TASKS.BLOCK.TIME') }}
                </span>
              </v-card-text>
              <v-card-text v-if="!isBlockEvent(selectedEvent)">
                <span class="text-h6"
                  >{{ $t('BOOKING.RESERVATIONS.CUSTOMER') }}:</span
                ><span class="text-h5">
                  {{ selectedEvent.person | personFormat }}</span
                >
              </v-card-text>
              <v-card-actions>
                <v-btn text color="secondary" @click="selectedOpen = false">
                  Cancel
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-sheet>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  priceFormat,
  dateTimeFormat,
  dateFormat,
  timeFormat,
  dateFormatBackend
} from '@/modules/_global/filters';
import moment from 'moment';
import { BLOCK } from '@/modules/booking_schedule/common/types';
import { DAY, HOUR } from '@/modules/booking_schedule/common/block_types';

export default {
  name: 'ExpensesList',

  filters: {
    priceFormat,
    dateFormat,
    dateTimeFormat,
    timeFormat,
    personFormat(value) {
      if (!value) return '';

      return value.first_name + ' ' + value.last_name;
    }
  },

  props: {
    bookingSchedule: {
      type: Array,
      default: () => []
    },
    options: {
      type: Object,
      default: () => {}
    }
  },

  data: () => ({
    weekdays: [1, 2, 3, 4, 5, 6, 0],
    date_from: null,
    localOptions: {},
    focus: '',
    type: 'month',

    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    events: []
  }),

  computed: {
    ...mapGetters('localization', ['lang']),
    ...mapState(['loading']),
    dateNow() {
      return moment(Date.now()).format('YYYY-MM-DD');
    },
    typeToLabel() {
      return {
        month: this.$t('BOOKING.SCHEDULE.MONTH'),
        week: this.$t('BOOKING.SCHEDULE.WEEK'),
        day: this.$t('BOOKING.SCHEDULE.DAY'),
        '4day': this.$t('BOOKING.SCHEDULE.DAYS')
      };
    },
    params() {
      const options = this.localOptions;

      return {
        date_from: options.date_from
      };
    }
  },

  watch: {
    localOptions: {
      handler() {
        this.refreshData();
      },
      deep: true
    },
    options: {
      handler() {
        this.refreshData();
      },
      deep: true
    },
    bookingSchedule: {
      handler() {
        this.updateRange();
      },
      deep: true
    }
  },
  mounted() {
    this.$refs.calendar.checkChange();
  },

  created() {
    this.localOptions.date_from = moment(
      moment(Date.now()).format('YYYY-MM')
    ).format('YYYY-MM-DD');
  },

  methods: {
    refreshData() {
      this.$emit('refresh', {
        params: Object.assign({}, { date_from: this.localOptions.date_from }),
        id: this.options.resource
      });
    },

    viewDay({ date }) {
      this.focus = date;
      this.type = 'day';
    },
    getEventColor(event) {
      return event.color;
    },
    setToday() {
      this.focus = '';
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },

    updateRange() {
      const events = [];
      let eventCount = this.bookingSchedule.length;

      for (let i = 0; i < eventCount; i++) {
        if (this.isBlockEvent(this.bookingSchedule[i])) {
          events.push({
            id: this.bookingSchedule[i].id,
            title: this.bookingSchedule[i].resource.title,
            name: '',
            start: new Date(
              dateFormatBackend(this.bookingSchedule[i].date_from) +
                ' ' +
                this.bookingSchedule[i].time_from
            ),
            time_from: this.bookingSchedule[i].time_from,
            time_to: this.bookingSchedule[i].time_to,
            end: new Date(
              dateFormatBackend(this.bookingSchedule[i].date_to) +
                ' ' +
                this.bookingSchedule[i].time_to
            ),
            timed: true,
            person: this.bookingSchedule[i].person,
            type: this.bookingSchedule[i].type,
            block_type: this.bookingSchedule[i].resource_type,
            color: 'grey'
          });
        } else {
          events.push({
            id: this.bookingSchedule[i].id,
            title: this.bookingSchedule[i].resource.title,
            name: '',
            start: new Date(
              this.bookingSchedule[i].date_from +
                ' ' +
                this.bookingSchedule[i].time_from
            ),
            time_from: this.bookingSchedule[i].time_from,
            time_to: this.bookingSchedule[i].time_to,
            end: new Date(
              this.bookingSchedule[i].date_to +
                ' ' +
                this.bookingSchedule[i].time_to
            ),
            timed: true,
            person: this.bookingSchedule[i].person,
            type: this.bookingSchedule[i].type,
            color: this.bookingSchedule[i].status.color
          });
        }
      }

      this.events = events;
    },
    updateData({ start, end }) {
      this.localOptions.date_from = start.date;
      this.refreshData();
    },

    isBlockEvent(event) {
      return event.type === BLOCK;
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        );
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(() => open()));
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    showSelectedEvent() {
      this.$emit('show', this.selectedEvent);
    },

    blockDayClass(event) {
      return {
        'ma-0': true,
        'pl-1': true,
        'v-event-block': event.block_type === DAY
      };
    },

    eventTime(event) {
      return `${dateTimeFormat(event.start)}-${event.time_to}`;
    }
  }
};
</script>

<style lang="scss">
.schedule {
  .v-event:has(.v-event-block) {
    height: 50% !important;
  }
}
</style>
